<template>
  <div>
    <!-- Tenant selector for superadmins -->
    <div v-if="isSuperAdmin" class="tenant-filter-container">
      <el-select
        v-model="selectedTenantId"
        filterable
        clearable
        :loading="loadingTenants"
        :placeholder="$t('current_reports.select_tenant')"
        @change="onTenantChange"
        style="width: 100%; max-width: 300px; margin-bottom: 20px;">
        <el-option
          v-for="tenant in tenants"
          :key="tenant.id"
          :label="tenant.name"
          :value="tenant.id">
        </el-option>
      </el-select>
    </div>

    <!-- No reports message for superadmins -->
    <el-alert
      v-if="isSuperAdmin && !selectedTenantId && !reports.length"
      type="info"
      :title="$t('current_reports.select_tenant_info')"
      :description="$t('current_reports.select_tenant_description')"
      show-icon
      :closable="false">
    </el-alert>

    <transition-group name="flip-list">
      <el-card
        v-on:click.native="onReportClick(report)"
        class="card"
        v-for="report in reports"
        :key="report.id"
        v-bind:style="{
                    '--indicator-color': report.warningOption.color
                }">
        <div class="header"
             :class="{'mobil-header':isMobil}">
          <!--  draw title only-->
          <template v-if="report.warningOption.type !== WarningOptionTypeEnum.MAINTENANCE">
            <h4 class="title">{{ report.subject }}</h4>
          </template>
          <!--  draw title and sub title-->
          <template v-else>
            <div>
              <h4 class="title has-sub-title">{{ report.subject }}</h4>
              <small v-if="report.maintenanceStart && report.maintenanceEnd" class="sub-title">
                {{ report.maintenanceStart | moment('dddd, Do MMMM YYYY | HH:mm') }} {{ $t('base.clock') }}
                -
                {{ report.maintenanceEnd | moment('dddd, Do MMMM YYYY | HH:mm') }} {{ $t('base.clock') }}
              </small>
            </div>
          </template>
          <div>
            <!-- draw receiving customer -->
            <el-tag
              class="customer-tag"
              :key="index"
              v-for="(selectedCustomer, index) in report.customers"
            >{{ selectedCustomer.name }}
            </el-tag>
          </div>
        </div>
        <div class="content">
          <p>{{ (report.updates[0] && report.updates[0].content) || report.content }}</p>
        </div>
        <div class="footer edition-information" :class="{'footer-mobil':isMobil}">
          <div>
            <label>
              <strong>
                {{ $t('current_reports.report_created') }}:
              </strong>
              <span>{{ report.createdAt | moment('dddd, Do MMMM YYYY | HH:mm') }}
              {{ $t('base.clock') }}</span>
            </label>
            <br>
            <label>
              <strong>
                {{ $t('current_reports.report_last_updated') }}:
              </strong>
              <span>{{ getReportLastUpdate(report) | moment('dddd, Do MMMM YYYY | HH:mm') }}
              {{ $t('base.clock') }}</span>
            </label>
          </div>
          <div>
            <label>
              <b>{{ $t('current_reports.report_creator') }}: </b>
              <span> {{ report.createdByName }} </span>
              <span v-if="report.createdByAccount ==null">({{ $t('settings.account.inactive') }})</span>
              <br>
              <template v-if="report.updatedBy !== report.author">
                <b>{{ $t('current_reports.report_updater') }}: </b>
                <span>{{ report.updatedByName }} </span>
                <span v-if="report.updatedByAccount == null">({{ $t('settings.account.inactive') }})</span>
              </template>
            </label>
          </div>
        </div>
      </el-card>
    </transition-group>
  </div>
</template>

<script lang="js">
import {createNamespacedHelpers} from 'vuex';
import {WarningOptionTypeEnum} from '@/enum/WarningOptionTypeEnum';

const mspStateMappers = createNamespacedHelpers('msp');
const applicationStateMappers = createNamespacedHelpers('application');
const authStateMappers = createNamespacedHelpers('auth');

export default {
  name: 'CurrentReports',
  data() {
    return {
      gotData: false,
      loading: null,
      pollingIntervalId: null,
      WarningOptionTypeEnum: WarningOptionTypeEnum,
      selectedTenantId: null,
      tenants: [],
      loadingTenants: false
    }
  },
  mounted() {
    this.getReportsAction();

    // Load tenants for super admin if already available
    if (this.isSuperAdmin) {
      this.loadTenants();
    }

    // start polling interval
    this.pollingIntervalId = setInterval(() => this.getReportsAction(), 1000 * 60 * 15); // 15 min.
    setTimeout(() => {
      if (!this.gotData) {
        this.loading = this.$loading({
          lock: true,
          text: this.$t('base.loading'),
          background: 'rgba(0, 0, 0, 0.7)'
        })
      }
    }, 1000);
  },
  beforeDestroy() {
    // stop polling from multiple starting
    if (this.pollingIntervalId) {
      clearInterval(this.pollingIntervalId);
    }
  },
  computed: {
    ...mspStateMappers.mapGetters({reports: 'getReports'}),
    ...applicationStateMappers.mapGetters({isMobil: 'isMobil'}),
    ...authStateMappers.mapGetters({isSuperAdmin: 'isSuperAdmin'})
  },
  watch: {
    // Watch for changes in isSuperAdmin to load tenants when user data becomes available
    isSuperAdmin: {
      handler(newValue) {
        if (newValue && this.tenants.length === 0) {
          this.loadTenants();
        }
      },
      immediate: true
    },
    reports() {
      this.gotData = true;
      if (this.loading) {
        this.loading.close();
      }
    }
  },
  methods: {
    ...mspStateMappers.mapActions(['getReportsAction']),
    getReportLastUpdate(report) {
      const updates = report.updates || [];

      const rawDates = [
        report.updatedAt,
        ...updates.map(u => u.updatedDate)
      ];

      const timestamps = rawDates
        .map(d => new Date(d).getTime())
        .filter(ts => !isNaN(ts));

      return timestamps.length > 0 ? Math.max(...timestamps) : report.updatedAt;
    },
    onReportClick(report) {
      this.$router.push({
        name: 'edit-report',
        params: {
          reportId: report.id
        }
      })
    },
    reportSaved(reportId) {
      this.$refs['reportEditState' + reportId][0].checked = false;
      this.getReportsAction();
    },
    loadTenants() {
      if (this.loadingTenants) return; // Prevent multiple concurrent loads

      this.loadingTenants = true;

      import('@/services/TenantService.class').then(({tenantService}) => {
        tenantService.get()
          .then(response => {
            this.tenants = response.data;
          })
          .catch(error => {
            console.error('Error loading tenants:', error);
            this.$message.error(this.$t('settings.genericHttpError'));
          })
          .finally(() => {
            this.loadingTenants = false;
          });
      });
    },
    onTenantChange() {
      // Re-fetch reports with the selected tenant
      const tenantParam = this.selectedTenantId ? {tenantId: this.selectedTenantId} : null;
      this.getReportsAction(tenantParam);
    }
  }
}
</script>

<style lang="scss" scoped>
.tenant-filter-container {
  margin-bottom: 20px;
}

.card {
  transition: transform 1s;
  /* CSS variable will be set by template for each card */
  --indicator-color: transparent;
  border-left: solid .75em var(--indicator-color);
  margin: 1em 0;
  cursor: pointer;

  .mobil-header {
    flex-flow: column-reverse;
  }

  .header {
    display: flex;
    align-content: center;
    justify-content: space-between;

    .customer-tag {
      margin-right: .5rem;
      margin-bottom: .5rem;
    }

    .title {
      margin-top: 0;

      &.has-sub-title {
        margin-bottom: 0;
      }
    }

    .sub-title {
      margin-top: 0.5em;
      font-size: 0.8em;
      color: #aaa;
    }
  }

  .content {
    p {
      white-space: pre-wrap;
    }
  }

  .footer {
    margin-top: 2em;
    width: 100%;
  }

  .footer-mobil {
    display: flex;
    flex-flow: column;
    align-items: flex-start;

    span {
      white-space: nowrap;
    }
  }

  label.report-edit-state {
    display: block;
  }

  .preview, .edit {
    transition: all 500ms ease;
  }

  input.report-edit-state {
    display: none;

    &:not(:checked) {
      ~ .preview {
        max-height: 100vh;
        opacity: 1;
        transition-delay: 250ms;
      }

      ~ .edit {
        position: relative;
        max-height: 0;
        opacity: 0;
        z-index: -1000;
      }
    }

    &:checked {
      ~ .preview {
        max-height: 0;
        opacity: 0;
      }

      ~ .edit {
        position: relative;
        max-height: 100vh;
        opacity: 1;
        transition-delay: 250ms;
        z-index: 1000;
      }
    }
  }
}
</style>
